import React, { useMemo, useState } from 'react';
import { Button, Table, Spin, Empty, message as antdMessage, Tag, Tooltip, Typography } from 'antd';
import { ColumnProps } from 'antd/es/table';
import { QualityDocumentsProps, DocumentsProps, QualityDocumentUpdateRequestPayload } from '../../../types/types';
import { get } from '@partsbadger/utils';

import SalesOrderStore from '../../../stores/SalesOrderStore';
import dayjs from 'dayjs';
import RequestDocumentUpdateModal from './RequestDocumentUpdateModal';

import { observer } from 'mobx-react';

import FileDownload from 'js-file-download';

const { Text } = Typography;

export interface QualityDocumentRow extends DocumentsProps {
    key: string;
    product_zid: number;
    product_name: string;
}

export const flattenQualityDocuments = (qualityDocuments: QualityDocumentsProps[]): QualityDocumentRow[] =>
    qualityDocuments.flatMap(product =>
        product.documents.map(document => ({
            ...document,
            key: `${product.product_zid}-${document.id}`,
            product_zid: product.product_zid,
            product_name: product.product_name,
        }))
    );

const isShippedOrder = (status?: string) =>
    status === 'Shipped in Partial' || status === 'Shipped in Full' || status === 'Shipped & Paid';

const getApprovedDocuments = (documents: DocumentsProps[]) =>
    documents.filter(document => document.status === 'approved');

const getQualityDocsFileName = (productName?: string) => {
    if (!productName) {
        return 'quality-docs.zip';
    }

    const slug = productName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');

    return `quality-docs-${slug || 'line-item'}.zip`;
};

const QualityDocuments: React.FC = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const [requestModalOpen, setRequestModalOpen] = useState(false);

    const documentRows = useMemo(() => flattenQualityDocuments(SalesOrderStore.quality_documents), [
        SalesOrderStore.quality_documents,
    ]);
    const approvedDocumentsCount = documentRows.filter(document => document.status === 'approved').length;
    const canDownloadAllDocuments = approvedDocumentsCount > 1;

    const getAllDocuments = async (product?: QualityDocumentsProps) => {
        setLoading(true);
        const productQuery = product ? `?product_zid=${product.product_zid}` : '';

        try {
            const response = await get(
                `/customer/sales-orders/${SalesOrderStore.order?.id}/download-all-quality-documents/${productQuery}`,
                { responseType: 'blob' }
            );
            FileDownload(response, getQualityDocsFileName(product?.product_name));
            setLoading(false);
            antdMessage.success('Documents downloaded');
        } catch (err) {
            setLoading(false);
            antdMessage.error(`Error - Get All Documents: ${err}`);
        }
    };

    const getDocument = async (document: DocumentsProps) => {
        if (!SalesOrderStore.order) {
            antdMessage.error('Unable to download document.');
            return;
        }

        setLoading(true);
        try {
            const response = await SalesOrderStore.downloadQualityDocumentBySalesOrderId(
                SalesOrderStore.order.id,
                document.id
            );
            FileDownload(response, document.file_name);
            antdMessage.success('Document downloaded');
        } catch (err) {
            antdMessage.error('Unable to download document.');
        } finally {
            setLoading(false);
        }
    };

    const requestDocumentUpdate = async (payload: QualityDocumentUpdateRequestPayload) => {
        if (!SalesOrderStore.order || payload.document_requests.length === 0) {
            antdMessage.error('Select at least one document.');
            return;
        }
        await SalesOrderStore.requestQualityDocumentUpdate(SalesOrderStore.order.id, payload);
    };

    const columns: ColumnProps<QualityDocumentsProps>[] = [
        {
            title: 'Product',
            dataIndex: 'product_name',
            key: 'product_name',
            render: (product_name, record) => {
                const approvedDocuments = getApprovedDocuments(record.documents);

                return (
                    <>
                        <div>{product_name}</div>
                        {approvedDocuments.length > 1 && (
                            <Button
                                className="mt-2"
                                loading={loading}
                                aria-label={`Download All for ${record.product_name}`}
                                onClick={() => {
                                    getAllDocuments(record);
                                }}
                            >
                                Download All
                            </Button>
                        )}
                    </>
                );
            },
        },
        {
            title: 'Document',
            dataIndex: 'document_type',
            key: 'document_type',
            width: '250px',
            render: (text, record) => (
                <>
                    {record.documents.map(document => (
                        <div className="truncate mb-2" style={{ width: '250px' }} key={document.id}>
                            <span className="text-black">{document.document_type}</span>
                        </div>
                    ))}
                </>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status, record) => (
                <>
                    {record.documents.map(document => (
                        <div className="mb-2" key={document.id}>
                            {document.status === 'approved' ? (
                                <Tag color="green">Approved</Tag>
                            ) : (
                                <Tag color="orange">Pending</Tag>
                            )}
                        </div>
                    ))}
                </>
            ),
        },
        {
            title: 'Date',
            dataIndex: 'created_time',
            key: 'created_time',
            render: (created_time, record) => (
                <>
                    {record.documents.map(document => (
                        <div className="mb-2" key={document.id}>
                            <span className="text-black truncate">
                                {dayjs(document.created_time).format('YYYY-MM-DD')}
                            </span>
                        </div>
                    ))}
                </>
            ),
        },
        {
            title: 'Action',
            dataIndex: 'download',
            key: 'download',
            render: (text, record) => {
                return loading ? (
                    <Spin spinning={loading} className="ml-5" />
                ) : (
                    <>
                        {record.documents.map(document =>
                            document.status === 'approved' ? (
                                <div className="mb-2" key={document.id}>
                                    <Tooltip title={document.file_name}>
                                        <a
                                            href="#"
                                            onClick={event => {
                                                event.preventDefault();
                                                getDocument(document);
                                            }}
                                            className="text-red-600 truncate"
                                        >
                                            Download
                                        </a>
                                    </Tooltip>
                                </div>
                            ) : (
                                <div className="mb-2" key={document.id}>
                                    <Text type="secondary">Available once approved</Text>
                                </div>
                            )
                        )}
                    </>
                );
            },
        },
    ];

    const showHeaderActions = documentRows.length > 0 || canDownloadAllDocuments;

    return (
        <>
            {showHeaderActions && (
                <div className="flex justify-end gap-2 mb-3">
                    {documentRows.length > 0 && (
                        <Button
                            type="primary"
                            onClick={() => {
                                setRequestModalOpen(true);
                            }}
                        >
                            Request document update
                        </Button>
                    )}
                    {canDownloadAllDocuments && (
                        <Button
                            loading={loading}
                            aria-label="Download All quality documents"
                            onClick={() => {
                                getAllDocuments();
                            }}
                        >
                            Download All
                        </Button>
                    )}
                </div>
            )}
            <Table<QualityDocumentsProps>
                rowKey={record => record.product_zid}
                pagination={false}
                columns={columns}
                className="border mb-3"
                dataSource={SalesOrderStore.quality_documents}
                scroll={window.innerWidth <= 1160 ? { x: 1160 } : { x: true }}
                locale={{
                    emptyText: (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={
                                <>
                                    {!isShippedOrder(SalesOrderStore.order?.status) && (
                                        <span style={{ color: '#000' }}>
                                            Documents will be available here once your order is shipped.
                                        </span>
                                    )}

                                    {isShippedOrder(SalesOrderStore.order?.status) && (
                                        <span style={{ color: '#000' }}>
                                            No quality documents have been uploaded for this order yet.
                                        </span>
                                    )}
                                </>
                            }
                        />
                    ),
                }}
            />
            <RequestDocumentUpdateModal
                open={requestModalOpen}
                order={SalesOrderStore.order}
                documents={documentRows}
                onCancel={() => {
                    setRequestModalOpen(false);
                }}
                onSubmit={requestDocumentUpdate}
            />
        </>
    );
};

export default observer(QualityDocuments);
